import React, { useState } from "react";
import { CalendarBlank } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { PRESETS, presetRange, rangeLabel, shiftDay, spanDays, todayBne } from "./dashUtils";

const pill = (on) => `text-xs px-3 py-1.5 rounded-full transition-colors ${on ? "bg-[#73bf44] text-[#050505] font-semibold" : "text-neutral-400 hover:text-white"}`;

export function RangeControls({ range, onRange, granularity, onGranularity }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState();
  const hourOk = spanDays(range) <= 14;
  // first click picks the start day, second click the end day
  const pick = (_range, day) => {
    if (!day) return;
    if (!draft?.from || draft.to) { setDraft({ from: day, to: undefined }); return; }
    const [from, to] = day < draft.from ? [day, draft.from] : [draft.from, day];
    setDraft({ from, to });
    onRange({ preset: "custom", start: format(from, "yyyy-MM-dd"), end: format(to, "yyyy-MM-dd") });
    setOpen(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-range-controls">
      <div className="flex items-center gap-0.5 bg-[#0A0A0A] border border-neutral-800 rounded-full p-1">
        {PRESETS.map((p) => (
          <button key={p.key} onClick={() => onRange(presetRange(p.key))} data-testid={`range-preset-${p.key}`} className={pill(range.preset === p.key)}>{p.label}</button>
        ))}
        <Popover open={open} onOpenChange={(v) => { setOpen(v); setDraft(undefined); }}>
          <PopoverTrigger asChild>
            <button data-testid="range-custom-btn" className={`${pill(range.preset === "custom")} inline-flex items-center gap-1.5`}>
              <CalendarBlank size={13} /> {range.preset === "custom" ? rangeLabel(range) : "Custom"}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0 bg-[#0F0F0F] border-neutral-800 text-neutral-200" data-testid="range-calendar">
            <Calendar mode="range" numberOfMonths={2} selected={draft || (range.start ? { from: parseISO(`${range.start}T12:00:00`), to: parseISO(`${range.end}T12:00:00`) } : undefined)}
              onSelect={pick} disabled={{ after: parseISO(`${todayBne()}T12:00:00`) }} defaultMonth={parseISO(`${range.start || shiftDay(todayBne(), -30)}T12:00:00`)} />
            <p className="px-4 pb-3 text-[0.65rem] text-neutral-500" data-testid="range-calendar-hint">{draft?.from && !draft.to ? `Start ${format(draft.from, "d MMM")} — now pick the end day` : "Pick a start day, then an end day (Brisbane time)."}</p>
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex items-center gap-0.5 bg-[#0A0A0A] border border-neutral-800 rounded-full p-1">
        <button onClick={() => onGranularity("hour")} disabled={!hourOk} title={hourOk ? "" : "Hourly view is available for up to 14 days"}
          data-testid="granularity-hour" className={`${pill(granularity === "hour" && hourOk)} disabled:opacity-30`}>Hour</button>
        <button onClick={() => onGranularity("day")} data-testid="granularity-day" className={pill(granularity === "day" || !hourOk)}>Day</button>
      </div>
    </div>
  );
}
